import type {
  MarketChartBar,
  MarketDomainServerMessage,
  MarketIntervalT,
  MarketLadder,
  MarketLiquidityHeatmapBucket,
  MarketLiquidityHeatmapSnapshot,
  MarketStats,
  MarketStreamSubscription,
  MarketTrade,
} from '@hypersense/contracts/market';
import { exhaustiveGuard } from '@hypersense/shared/typing';

export type MarketFeedStreamStatusT = 'connecting' | 'live' | 'offline';

export interface MarketFeedStreamStateT {
  bars: MarketChartBar[];
  errorMessage: string | null;
  hasBarsSnapshot: boolean;
  laddersByGrouping: Record<string, MarketLadder | null>;
  lastMessageAtMs: number | null;
  liquidityByWindowMinutes: Record<string, MarketLiquidityHeatmapSnapshot>;
  requestedBarsInterval: MarketIntervalT | null;
  requestedLiquidityWindowMinutes: number | null;
  stats: MarketStats | null;
  status: MarketFeedStreamStatusT;
  trades: MarketTrade[];
}

export function createInitialMarketFeedStreamState(): MarketFeedStreamStateT {
  return {
    bars: [],
    errorMessage: null,
    hasBarsSnapshot: false,
    laddersByGrouping: {},
    lastMessageAtMs: null,
    liquidityByWindowMinutes: {},
    requestedBarsInterval: null,
    requestedLiquidityWindowMinutes: null,
    stats: null,
    status: 'connecting',
    trades: [],
  };
}

export function applyMarketFeedSubscriptionChange(input: {
  currentState: MarketFeedStreamStateT;
  nextSubscription: MarketStreamSubscription;
  previousSubscription: MarketStreamSubscription | null;
}) {
  const nextBarsInterval =
    input.nextSubscription.resources.bars?.interval ?? null;
  const previousBarsInterval =
    input.previousSubscription?.resources.bars?.interval ?? null;
  const nextLiquidityWindowMinutes =
    input.nextSubscription.resources.liquidity?.windowMinutes ?? null;
  const previousLiquidityWindowMinutes =
    input.previousSubscription?.resources.liquidity?.windowMinutes ?? null;

  if (
    previousBarsInterval !== nextBarsInterval ||
    previousLiquidityWindowMinutes !== nextLiquidityWindowMinutes
  ) {
    return {
      ...input.currentState,
      errorMessage: null,
      bars:
        previousBarsInterval !== nextBarsInterval
          ? []
          : input.currentState.bars,
      hasBarsSnapshot:
        previousBarsInterval !== nextBarsInterval
          ? false
          : input.currentState.hasBarsSnapshot,
      requestedBarsInterval: nextBarsInterval,
      requestedLiquidityWindowMinutes: nextLiquidityWindowMinutes,
    };
  }

  if (
    input.currentState.requestedBarsInterval !== nextBarsInterval ||
    input.currentState.requestedLiquidityWindowMinutes !==
      nextLiquidityWindowMinutes
  ) {
    return {
      ...input.currentState,
      requestedBarsInterval: nextBarsInterval,
      requestedLiquidityWindowMinutes: nextLiquidityWindowMinutes,
    };
  }

  return input.currentState;
}

export function areMarketFeedSubscriptionsEqual(
  left: MarketStreamSubscription,
  right: MarketStreamSubscription,
) {
  const leftBars = left.resources.bars;
  const rightBars = right.resources.bars;
  const leftLiquidity = left.resources.liquidity;
  const rightLiquidity = right.resources.liquidity;
  const leftLadder = left.resources.ladder;
  const rightLadder = right.resources.ladder;
  const leftStats = left.resources.stats;
  const rightStats = right.resources.stats;
  const leftTrades = left.resources.trades;
  const rightTrades = right.resources.trades;

  return (
    left.type === right.type &&
    left.asset === right.asset &&
    leftBars?.interval === rightBars?.interval &&
    leftLiquidity?.windowMinutes === rightLiquidity?.windowMinutes &&
    leftLadder?.grouping === rightLadder?.grouping &&
    leftLadder?.levels === rightLadder?.levels &&
    leftStats === rightStats &&
    leftTrades?.limit === rightTrades?.limit
  );
}

export function reduceMarketFeedStreamMessage(input: {
  currentState: MarketFeedStreamStateT;
  maxTradeCount: number;
  message: MarketDomainServerMessage;
}): MarketFeedStreamStateT {
  const nowMs = Date.now();

  switch (input.message.type) {
    case 'market.stats_snapshot':
      return {
        ...input.currentState,
        errorMessage: null,
        lastMessageAtMs: nowMs,
        stats: input.message.stats,
        status: 'live',
      };
    case 'market.stats':
      return {
        ...input.currentState,
        lastMessageAtMs: nowMs,
        stats: input.message.stats,
        status: 'live',
      };
    case 'market.bars_snapshot':
      if (input.currentState.requestedBarsInterval !== input.message.interval) {
        return input.currentState;
      }

      return {
        ...input.currentState,
        bars: input.message.bars,
        errorMessage: null,
        hasBarsSnapshot: true,
        lastMessageAtMs: nowMs,
        status: 'live',
      };
    case 'market.bar':
      if (input.currentState.requestedBarsInterval !== input.message.interval) {
        return input.currentState;
      }

      if (!input.currentState.hasBarsSnapshot) {
        return input.currentState;
      }

      return {
        ...input.currentState,
        bars: mergeBars({
          currentBars: input.currentState.bars,
          nextBar: input.message.bar,
        }),
        lastMessageAtMs: nowMs,
        status: 'live',
      };
    case 'market.ladder_snapshot':
    case 'market.ladder':
      return applyLadderMessage({
        currentState: input.currentState,
        errorMessage: null,
        grouping: input.message.grouping,
        ladder: input.message.ladder,
        nowMs,
      });
    case 'market.trades_snapshot':
      return {
        ...input.currentState,
        errorMessage: null,
        lastMessageAtMs: nowMs,
        status: 'live',
        trades: input.message.trades,
      };
    case 'market.trades_append':
      return {
        ...input.currentState,
        lastMessageAtMs: nowMs,
        status: 'live',
        trades: prependTrades({
          currentTrades: input.currentState.trades,
          maxTradeCount: input.maxTradeCount,
          nextTrades: input.message.trades,
        }),
      };
    case 'error':
      return {
        ...input.currentState,
        errorMessage: input.message.error,
        status: 'offline',
      };
    case 'heartbeat':
      return input.currentState;
    case 'market.liquidity_snapshot':
      return {
        ...input.currentState,
        errorMessage: null,
        lastMessageAtMs: nowMs,
        liquidityByWindowMinutes: {
          ...input.currentState.liquidityByWindowMinutes,
          [readLiquidityWindowKey(input.message.windowMinutes)]: input.message,
        },
        status: 'live',
      };
    case 'market.liquidity_delta':
      return applyLiquidityDeltaMessage({
        currentState: input.currentState,
        message: input.message,
        nowMs,
      });
    default:
      return exhaustiveGuard(input.message);
  }
}

function applyLadderMessage(input: {
  currentState: MarketFeedStreamStateT;
  errorMessage: string | null;
  grouping: number;
  ladder: MarketLadder | null;
  nowMs: number;
}): MarketFeedStreamStateT {
  return {
    ...input.currentState,
    errorMessage: input.errorMessage,
    laddersByGrouping: {
      ...input.currentState.laddersByGrouping,
      [readLadderGroupingKey(input.grouping)]: input.ladder,
    },
    lastMessageAtMs: input.nowMs,
    status: 'live',
  };
}

const MAX_BARS_COUNT = 500;

function mergeBars(input: {
  currentBars: MarketChartBar[];
  nextBar: MarketChartBar;
}) {
  if (input.currentBars.length === 0) {
    return [input.nextBar];
  }

  const lastBar = input.currentBars[input.currentBars.length - 1];

  if (lastBar?.openTimeMs === input.nextBar.openTimeMs) {
    const nextBars = input.currentBars.slice();
    nextBars[nextBars.length - 1] = input.nextBar;
    return nextBars;
  }

  if (lastBar !== undefined && lastBar.openTimeMs < input.nextBar.openTimeMs) {
    const nextBars = [...input.currentBars, input.nextBar];

    if (nextBars.length > MAX_BARS_COUNT) {
      return nextBars.slice(nextBars.length - MAX_BARS_COUNT);
    }

    return nextBars;
  }

  const mergedBars: MarketChartBar[] = [];
  let didInsertNextBar = false;

  for (const bar of input.currentBars) {
    if (bar.openTimeMs === input.nextBar.openTimeMs) {
      mergedBars.push(input.nextBar);
      didInsertNextBar = true;
      continue;
    }

    if (!didInsertNextBar && bar.openTimeMs > input.nextBar.openTimeMs) {
      mergedBars.push(input.nextBar);
      didInsertNextBar = true;
    }

    mergedBars.push(bar);
  }

  if (!didInsertNextBar) {
    mergedBars.push(input.nextBar);
  }

  return mergedBars;
}

function prependTrades(input: {
  currentTrades: MarketTrade[];
  maxTradeCount: number;
  nextTrades: MarketTrade[];
}) {
  const tradesById = new Map<string, MarketTrade>();

  for (const trade of input.nextTrades) {
    tradesById.set(trade.tradeId, trade);
  }

  for (const trade of input.currentTrades) {
    if (!tradesById.has(trade.tradeId)) {
      tradesById.set(trade.tradeId, trade);
    }
  }

  return [...tradesById.values()]
    .sort((left, right) => {
      return right.timestampMs - left.timestampMs;
    })
    .slice(0, input.maxTradeCount);
}

function readLadderGroupingKey(grouping: number) {
  return String(grouping);
}

function applyLiquidityDeltaMessage(input: {
  currentState: MarketFeedStreamStateT;
  message: Extract<
    MarketDomainServerMessage,
    { type: 'market.liquidity_delta' }
  >;
  nowMs: number;
}) {
  const liquidityWindowKey = readLiquidityWindowKey(
    input.message.windowMinutes,
  );
  const currentSnapshot =
    input.currentState.liquidityByWindowMinutes[liquidityWindowKey];

  if (currentSnapshot === undefined) {
    return input.currentState;
  }

  const nextSnapshot = {
    ...currentSnapshot,
    annotations: input.message.annotations,
    buckets: mergeLiquidityBuckets({
      currentBuckets: currentSnapshot.buckets,
      latestBucket: input.message.latestBucket,
      requestedWindowStartMs: input.message.coverage.requestedWindowStartMs,
    }),
    coverage: input.message.coverage,
    priceBucketSize: input.message.priceBucketSize,
    scale: input.message.scale,
    snapshotTimeMs: input.message.snapshotTimeMs,
    version: input.message.version,
    windowMinutes: input.message.windowMinutes,
  } satisfies MarketLiquidityHeatmapSnapshot;

  const nextState: MarketFeedStreamStateT = {
    ...input.currentState,
    lastMessageAtMs: input.nowMs,
    liquidityByWindowMinutes: {
      ...input.currentState.liquidityByWindowMinutes,
      [liquidityWindowKey]: nextSnapshot,
    },
    status: 'live',
  };

  return nextState;
}

function mergeLiquidityBuckets(input: {
  currentBuckets: MarketLiquidityHeatmapSnapshot['buckets'];
  latestBucket: MarketLiquidityHeatmapBucket;
  requestedWindowStartMs: number;
}) {
  const nextBuckets = input.currentBuckets
    .filter((bucket) => {
      return bucket.timeEndMs > input.requestedWindowStartMs;
    })
    .filter((bucket) => {
      return bucket.timeStartMs !== input.latestBucket.timeStartMs;
    });

  nextBuckets.push(input.latestBucket);
  nextBuckets.sort((left, right) => left.timeStartMs - right.timeStartMs);

  return nextBuckets;
}

function readLiquidityWindowKey(windowMinutes: number) {
  return String(windowMinutes);
}
